import { useState, useCallback } from "react";
import { Link } from "@corpuscomplete/web/components/link";
import { useQuery } from "@tanstack/react-query";
import { Page, PageBody, PageHeader, PageContent } from "@corpuscomplete/web/components/layout";
import { Text } from "@corpuscomplete/web-ui/components/text";
import { Button } from "@corpuscomplete/web-ui/components/button";
import { Badge } from "@corpuscomplete/web-ui/components/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@corpuscomplete/web-ui/components/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@corpuscomplete/web-ui/components/dropdown-menu";
import { adminRpc } from "@corpuscomplete/web/rpc/client";
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import type { AIIssue } from "@corpuscomplete/core/ai/ai-issue";

type StatusFilter = AIIssue.Status | "all";
type TypeFilter = AIIssue.Type | "all";
type RootCauseFilter = AIIssue.RootCause | "all";

export function AIIssuesPage() {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
  const [rootCauseFilter, setRootCauseFilter] = useState<RootCauseFilter>("all");
  const [cursors, setCursors] = useState<string[]>([]);
  const currentCursor = cursors[cursors.length - 1];

  const { data, isLoading, error } = useQuery(
    adminRpc.aiIssues.list.queryOptions({
      input: {
        status: statusFilter === "all" ? undefined : statusFilter,
        type: typeFilter === "all" ? undefined : typeFilter,
        rootCause: rootCauseFilter === "all" ? undefined : rootCauseFilter,
        cursor: currentCursor,
        limit: 20,
      },
    }),
  );

  const handleStatusChange = useCallback((value: string) => {
    setStatusFilter(value as StatusFilter);
    setCursors([]);
  }, []);

  const handleTypeChange = useCallback((value: string) => {
    setTypeFilter(value as TypeFilter);
    setCursors([]);
  }, []);

  const handleRootCauseChange = useCallback((value: string) => {
    setRootCauseFilter(value as RootCauseFilter);
    setCursors([]);
  }, []);

  const handleNextPage = useCallback(() => {
    const cursor = data?.nextCursor;
    if (cursor) {
      setCursors((prev) => [...prev, cursor]);
    }
  }, [data?.nextCursor]);

  const handlePrevPage = useCallback(() => {
    setCursors((prev) => prev.slice(0, -1));
  }, []);

  const hasPrevPage = cursors.length > 0;
  const hasNextPage = Boolean(data?.nextCursor);

  return (
    <Page>
      <PageBody>
        <PageHeader>
          <Text variant="h5">AI Issues</Text>
        </PageHeader>
        <PageContent className="p-4">
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger
                  render={
                    <Button variant="outline" size="sm">
                      Status: {statusFilter === "all" ? "All" : statusFilter}
                      <ChevronDownIcon className="ml-1 size-3" />
                    </Button>
                  }
                />
                <DropdownMenuContent>
                  <DropdownMenuRadioGroup value={statusFilter} onValueChange={handleStatusChange}>
                    <DropdownMenuRadioItem value="all">All</DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="pending">Pending</DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="notable">Notable</DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="dismissed">Dismissed</DropdownMenuRadioItem>
                  </DropdownMenuRadioGroup>
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger
                  render={
                    <Button variant="outline" size="sm">
                      Type: {typeFilter === "all" ? "All" : typeFilter}
                      <ChevronDownIcon className="ml-1 size-3" />
                    </Button>
                  }
                />
                <DropdownMenuContent>
                  <DropdownMenuRadioGroup value={typeFilter} onValueChange={handleTypeChange}>
                    <DropdownMenuRadioItem value="all">All</DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="meal-item-link">
                      Meal Item Link
                    </DropdownMenuRadioItem>
                  </DropdownMenuRadioGroup>
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger
                  render={
                    <Button variant="outline" size="sm">
                      Root Cause:{" "}
                      {rootCauseFilter === "all" ? "All" : formatRootCause(rootCauseFilter)}
                      <ChevronDownIcon className="ml-1 size-3" />
                    </Button>
                  }
                />
                <DropdownMenuContent>
                  <DropdownMenuRadioGroup
                    value={rootCauseFilter}
                    onValueChange={handleRootCauseChange}
                  >
                    <DropdownMenuRadioItem value="all">All</DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="data-gap">Data Gap</DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="search-issue">Search Issue</DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="model-issue">Model Issue</DropdownMenuRadioItem>
                  </DropdownMenuRadioGroup>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {isLoading && (
              <div className="py-8 text-center text-sm text-muted-foreground">Loading...</div>
            )}

            {error && (
              <div className="py-8 text-center text-sm text-destructive">
                Failed to load issues: {error.message}
              </div>
            )}

            {data && data.items.length === 0 && (
              <div className="py-8 text-center text-sm text-muted-foreground">
                No issues found matching the current filters.
              </div>
            )}

            {data && data.items.length > 0 && (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Status</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Root Cause</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="w-20" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.items.map((issue) => (
                      <IssueRow key={issue.id} issue={issue} />
                    ))}
                  </TableBody>
                </Table>

                <div className="flex items-center justify-between">
                  <Text variant="sm" className="text-muted-foreground">
                    Showing {data.items.length} issue{data.items.length !== 1 ? "s" : ""}
                  </Text>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handlePrevPage}
                      disabled={!hasPrevPage}
                    >
                      <ChevronLeftIcon className="size-4" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleNextPage}
                      disabled={!hasNextPage}
                    >
                      Next
                      <ChevronRightIcon className="size-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </PageContent>
      </PageBody>
    </Page>
  );
}

function IssueRow({ issue }: { issue: AIIssue.Info }) {
  const createdDate = issue.timeCreated.toLocaleString("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
  });

  return (
    <TableRow>
      <TableCell>
        <StatusBadge status={issue.status} />
      </TableCell>
      <TableCell>
        <Badge variant="outline">{issue.type}</Badge>
      </TableCell>
      <TableCell>
        {issue.rootCause ? (
          <RootCauseBadge rootCause={issue.rootCause} />
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className="max-w-xs truncate">{issue.reason || "—"}</TableCell>
      <TableCell className="text-muted-foreground">{createdDate}</TableCell>
      <TableCell>
        <Link to="/admin/ai-issues/$id" params={{ id: issue.id }}>
          <Button variant="ghost" size="xs">
            View
          </Button>
        </Link>
      </TableCell>
    </TableRow>
  );
}

function StatusBadge({ status }: { status: AIIssue.Status }) {
  switch (status) {
    case "pending":
      return <Badge variant="outline">Pending</Badge>;
    case "notable":
      return <Badge variant="default">Notable</Badge>;
    case "dismissed":
      return <Badge variant="secondary">Dismissed</Badge>;
  }
}

function RootCauseBadge({ rootCause }: { rootCause: AIIssue.RootCause }) {
  switch (rootCause) {
    case "data-gap":
      return <Badge variant="outline">Data Gap</Badge>;
    case "search-issue":
      return <Badge variant="outline">Search Issue</Badge>;
    case "model-issue":
      return <Badge variant="outline">Model Issue</Badge>;
  }
}

function formatRootCause(rootCause: AIIssue.RootCause): string {
  switch (rootCause) {
    case "data-gap":
      return "Data Gap";
    case "search-issue":
      return "Search Issue";
    case "model-issue":
      return "Model Issue";
  }
}
